export interface FeeTier {
    upToPrice: number | null;
    feeType: 'percent' | 'fixed';
    feeValue: number;
}

export interface ResolvedFee {
    feeType: 'percent' | 'fixed' | 'tiered';
    feeValue: number;
    feeLabel: string;
    feeTiers?: FeeTier[];
}

export function calculateFeePerUnit(fee: ResolvedFee | null | undefined, price: number): number {
    if (!fee || price <= 0) return 0;
    if (fee.feeType === 'tiered') {
        const tiers = [...(fee.feeTiers || [])].sort((a, b) => {
            if (a.upToPrice == null) return 1;
            if (b.upToPrice == null) return -1;
            return a.upToPrice - b.upToPrice;
        });
        const tier = tiers.find((t) => t.upToPrice == null || price <= t.upToPrice) || tiers[tiers.length - 1];
        if (!tier || !tier.feeValue) return 0;
        return tier.feeType === 'fixed' ? tier.feeValue : (price * tier.feeValue) / 100;
    }
    if (!fee.feeValue) return 0;
    return fee.feeType === 'fixed' ? fee.feeValue : (price * fee.feeValue) / 100;
}

const STRIPE_PERCENT_FALLBACK = 0.029;
const STRIPE_FIXED_FALLBACK = 0.3;

/**
 * Stripe's cost when the buyer is charged enough that `net` (tickets + service fee) remains after Stripe
 * takes its cut. Mirrors the server's gross-up, rounded up to the cent.
 */
export function stripeFeeForNet(net: number, info?: { stripePercent?: number; stripeFixed?: number }): number {
    if (net <= 0) return 0;
    const percent = Number(info?.stripePercent) || STRIPE_PERCENT_FALLBACK;
    const fixed = info?.stripeFixed ?? STRIPE_FIXED_FALLBACK;
    return Math.ceil((((net + fixed) / (1 - percent)) * percent + fixed) * 100 - 1e-9) / 100;
}

const trimNumber = (value: number) => String(Math.round(value * 100) / 100);

/** The service fee formula in one summed expression: Stripe's 2.9% + $0.30 plus the organizer's own fee. */
export function describeFeeFormula(fee: ResolvedFee | null | undefined, symbol = '$'): string {
    const percent = STRIPE_PERCENT_FALLBACK * 100 + (fee && fee.feeType === 'percent' ? fee.feeValue || 0 : 0);
    const fixed = STRIPE_FIXED_FALLBACK + (fee && fee.feeType === 'fixed' ? fee.feeValue || 0 : 0);
    const summed = `${trimNumber(percent)}% + ${symbol}${fixed.toFixed(2)} per transaction`;
    return fee?.feeType === 'tiered' ? `${summed} plus the tier fee for the ticket price` : summed;
}
